import React, { useEffect, useMemo, useState } from 'react';
import { Alert } from '../../../utils/Alert';
import { asList } from '../../../utils/apiList';
import MainLayout from '../../../components/layout/MainLayout';
import AdminSidebar from '../AdminSidebar';
import axios from 'axios';
import { FaPlus, FaBullseye, FaEdit, FaTrash, FaTimes } from 'react-icons/fa';
import '../Admin.css';

import { API_URL } from '../../../config';
import { Pagination, usePagination } from '../../../components/ui/Pagination';
interface DashboardPageProps {
  user: {
    name: string;
    role: string;
    avatar?: string;
  };
  onLogout: () => void;
}

type QuestionType = 'single_correct_A' | 'single_correct_B' | 'multiple_correct' | 'integer_type' | 'numerical_type' | 'three_column_match' | 'comprehension' | 'matrix_match';

type QuestionTypeConfig = {
  question_type: QuestionType;
  num_questions: number;
  marks: number;
  negative_marks: number;
  mandatory_questions: number;
};

type TargetExam = {
  id: string | number;
  name: string;
  description: string;
  use_question_type_config?: boolean;
  question_type_settings?: any[]; // Can be in snake_case or camelCase from API
  total_questions?: number;
  enable_negative_marking?: boolean;
  marks_per_question?: number;
  negative_marks_per_question?: number;
  created_at?: string;
  updated_at?: string | null;
  createdAt?: Date; // for local display formatting
};

const TargetExamPage: React.FC<DashboardPageProps> = ({ user, onLogout }) => {
  const [targetExams, setTargetExams] = useState<TargetExam[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  // Notification state (success/error/info)
  const [notification, setNotification] = useState<{
    type: 'success' | 'error' | 'info';
    message: string;
    show: boolean;
  } | null>(null);

  const [form, setForm] = useState({
    name: '',
    description: '',
    useQuestionTypeConfig: false,
    questionTypeConfigs: [] as QuestionTypeConfig[],
    totalQuestions: 0,
    enableNegativeMarking: false,
    marksPerQuestion: 0,
    negativeMarksPerQuestion: 0,
  });
  const [formErrors, setFormErrors] = useState<{ [k: string]: string }>({});

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editExam, setEditExam] = useState<TargetExam | null>(null);

  const [deleteConfirmation, setDeleteConfirmation] = useState<{ show: boolean; id: string | number | null; name: string }>(
    { show: false, id: null, name: '' }
  );

  const resetForm = () => {
    setForm({
      name: '',
      description: '',
      useQuestionTypeConfig: false,
      questionTypeConfigs: [],
      totalQuestions: 0,
      enableNegativeMarking: false,
      marksPerQuestion: 0,
      negativeMarksPerQuestion: 0,
    });
    setFormErrors({});
  };

  // Show notification helper
  const showNotification = (type: 'success' | 'error' | 'info', message: string) => {
    if (type === 'success') {
      Alert.success('Success!', message);
    } else if (type === 'error') {
      Alert.error('Oops...', message);
    } else {
      Alert.warning('Notice', message);
    }
  };

  useEffect(() => {
    fetchTargetExams();
  }, []);

  useEffect(() => {
    // Persist to localStorage (temporary until API integration of GET)
    const serializable = targetExams.map((t) => ({
      id: t.id,
      name: t.name,
      description: t.description,
      created_at: t.created_at || t.createdAt?.toISOString(),
      updated_at: t.updated_at ?? null,
    }));
    localStorage.setItem('target_exams', JSON.stringify(serializable));
  }, [targetExams]);

  const questionTypeLabels: Record<QuestionType, string> = {
    single_correct_A: 'Single Correct A',
    single_correct_B: 'Single Correct B',
    multiple_correct: 'Multiple Correct',
    integer_type: 'Integer Type',
    numerical_type: 'Numerical Type',
    three_column_match: 'Three Column Match',
    comprehension: 'Comprehension',
    matrix_match: 'Matrix Match',
  };

  const availableQuestionTypes: QuestionType[] = [
    'single_correct_A',
    'single_correct_B',
    'multiple_correct',
    'integer_type',
    'numerical_type',
    'three_column_match',
    'comprehension',
    'matrix_match',
  ];

  const addQuestionTypeConfig = () => {
    const availableTypes = availableQuestionTypes.filter(
      (type) => !form.questionTypeConfigs.some((config) => config.question_type === type)
    );
    if (availableTypes.length > 0) {
      setForm((prev) => ({
        ...prev,
        questionTypeConfigs: [
          ...prev.questionTypeConfigs,
          {
            question_type: availableTypes[0],
            num_questions: 0,
            marks: 0,
            negative_marks: 0,
            mandatory_questions: 0
          },
        ],
      }));
    }
  };

  const removeQuestionTypeConfig = (index: number) => {
    setForm((prev) => ({
      ...prev,
      questionTypeConfigs: prev.questionTypeConfigs.filter((_, i) => i !== index),
    }));
  };

  const updateQuestionTypeConfig = (index: number, field: keyof QuestionTypeConfig, value: any) => {
    setForm((prev) => ({
      ...prev,
      questionTypeConfigs: prev.questionTypeConfigs.map((config, i) =>
        i === index ? { ...config, [field]: value } : config
      ),
    }));
  };

  const validate = () => {
    const errors: { [k: string]: string } = {};
    if (!form.name.trim()) errors.name = 'Name is required';

    if (form.useQuestionTypeConfig) {
      if (form.questionTypeConfigs.length === 0) {
        errors.questionTypeConfigs = 'At least one question type must be configured';
      } else {
        form.questionTypeConfigs.forEach((config, index) => {
          if (config.num_questions <= 0) {
            errors[`questionTypeConfig_${index}_num_questions`] = 'Number of questions must be greater than 0';
          }
          if (config.marks <= 0) {
            errors[`questionTypeConfig_${index}_marks`] = 'Marks must be greater than 0';
          }
          if (form.enableNegativeMarking && config.negative_marks < 0) {
            errors[`questionTypeConfig_${index}_negative_marks`] = 'Negative marks cannot be negative';
          }
          if (config.mandatory_questions < 0) {
            errors[`questionTypeConfig_${index}_mandatory`] = 'Mandatory questions cannot be negative';
          }
          if (config.mandatory_questions > config.num_questions) {
            errors[`questionTypeConfig_${index}_mandatory`] = 'Mandatory questions cannot exceed total questions';
          }
        });
      }
    } else {
      if (form.totalQuestions <= 0) {
        errors.totalQuestions = 'Total questions must be greater than 0';
      }
    }

    if (form.enableNegativeMarking) {
      // Only validate global marks if NOT using question type config
      if (!form.useQuestionTypeConfig) {
        if (form.marksPerQuestion <= 0) {
          errors.marksPerQuestion = 'Marks per question must be greater than 0';
        }
        if (form.negativeMarksPerQuestion < 0) {
          errors.negativeMarksPerQuestion = 'Negative marks per question cannot be negative';
        }
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return {
      headers: {
        Authorization: `Bearer ${token}`,
        accept: 'application/json',
        'Content-Type': 'application/json',
      },
    };
  };

  const fetchTargetExams = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_URL}/api/target-exams/`, getAuthHeader());
      setTargetExams(asList(response.data));
    } catch (err: any) {
      Alert.error('Error', 'Failed to load target exams');
      setError(null);
      // Fallback: seed from localStorage if present
      const raw = localStorage.getItem('target_exams');
      if (raw) {
        try {
          const parsed: any[] = JSON.parse(raw);
          setTargetExams(
            parsed.map((r) => ({
              id: r.id,
              name: r.name,
              description: r.description,
              created_at: r.created_at,
              createdAt: r.created_at ? new Date(r.created_at) : new Date(),
            }))
          );
        } catch (_) {
          // ignore parsing error
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = async () => {
    console.log('handleCreate called', { form, errors: formErrors });
    if (!validate()) {
      console.log('Validation failed', formErrors);
      return;
    }
    console.log('Validation passed, making API call');
    setIsSaving(true);
    setError(null);
    try {
      const payload: any = {
        name: form.name.trim(),
        description: form.description.trim(),
        use_question_type_config: form.useQuestionTypeConfig,
        enable_negative_marking: form.enableNegativeMarking,
      };

      if (form.useQuestionTypeConfig) {
        payload.question_type_settings = form.questionTypeConfigs.map((config) => {
          const setting: any = {
            question_type: config.question_type,
            num_questions: config.num_questions,
            marks: config.marks,
            mandatory_questions: config.mandatory_questions,
          };
          // Only include negative_marks if negative marking is enabled
          if (form.enableNegativeMarking) {
            setting.negative_marks = config.negative_marks;
          }
          return setting;
        });
      } else {
        payload.total_questions = form.totalQuestions;
        // Include marks if negative marking is enabled (only for simple total questions)
        if (form.enableNegativeMarking) {
          payload.marks_per_question = form.marksPerQuestion;
          payload.negative_marks_per_question = form.negativeMarksPerQuestion;
        }
      }

      console.log('API Payload:', payload);
      console.log('API URL:', `${API_URL}/api/target-exams/`);

      const response = await axios.post(
        `${API_URL}/api/target-exams/`,
        payload,
        getAuthHeader()
      );

      console.log('API Response:', response.data);

      const created: TargetExam = response.data;
      setTargetExams((prev) => [
        {
          ...created,
          createdAt: created.created_at ? new Date(created.created_at) : new Date(),
        },
        ...prev,
      ]);
      setIsModalOpen(false);
      resetForm();
      Alert.success('Success', `Target exam "${created.name}" created successfully`);
    } catch (err: any) {
      console.error('API Error:', err);
      console.error('Error Response:', err?.response);
      let errorMessage = 'Failed to create target exam';
      if (err?.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err?.response?.data) {
        if (typeof err.response.data === 'string') {
          errorMessage = err.response.data;
        } else if (err.response.data.message) {
          errorMessage = err.response.data.message;
        } else {
          errorMessage = JSON.stringify(err.response.data);
        }
      } else if (err?.message) {
        errorMessage = err.message;
      }
      Alert.error('Error', errorMessage);
      setError(null);
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditClick = (exam: TargetExam) => {
    setEditExam({
      ...exam,
      use_question_type_config: exam.use_question_type_config ?? false,
      question_type_settings: exam.question_type_settings || [],
      total_questions: exam.total_questions || 0,
      enable_negative_marking: exam.enable_negative_marking ?? false,
      marks_per_question: exam.marks_per_question || 0,
      negative_marks_per_question: exam.negative_marks_per_question || 0,
    });
    setIsEditModalOpen(true);
    setFormErrors({});
  };

  const validateEdit = (exam: TargetExam) => {
    const errors: { [k: string]: string } = {};
    if (!exam.name?.trim()) errors.name = 'Name is required';

    if (exam.use_question_type_config) {
      if (!exam.question_type_settings || exam.question_type_settings.length === 0) {
        errors.questionTypeConfigs = 'At least one question type must be configured';
      } else {
        exam.question_type_settings.forEach((config: any, index: number) => {
          const numQuestions = config.num_questions || 0;
          const marks = config.marks || 0;
          const negativeMarks = config.negative_marks || 0;
          const mandatoryQuestions = config.mandatory_questions || 0;

          if (numQuestions <= 0) {
            errors[`questionTypeConfig_${index}_num_questions`] = 'Number of questions must be greater than 0';
          }
          if (marks <= 0) {
            errors[`questionTypeConfig_${index}_marks`] = 'Marks must be greater than 0';
          }
          if (exam.enable_negative_marking && negativeMarks < 0) {
            errors[`questionTypeConfig_${index}_negative_marks`] = 'Negative marks cannot be negative';
          }
          if (mandatoryQuestions < 0) {
            errors[`questionTypeConfig_${index}_mandatory`] = 'Mandatory questions cannot be negative';
          }
          if (mandatoryQuestions > numQuestions) {
            errors[`questionTypeConfig_${index}_mandatory`] = 'Mandatory questions cannot exceed total questions';
          }
        });
      }
    } else {
      const totalQuestions = exam.total_questions || 0;
      if (totalQuestions <= 0) {
        errors.totalQuestions = 'Total questions must be greater than 0';
      }
      // Only validate global marks if NOT using question type config
      if (exam.enable_negative_marking) {
        const marksPerQuestion = exam.marks_per_question || 0;
        const negativeMarksPerQuestion = exam.negative_marks_per_question || 0;
        if (marksPerQuestion <= 0) {
          errors.marksPerQuestion = 'Marks per question must be greater than 0';
        }
        if (negativeMarksPerQuestion < 0) {
          errors.negativeMarksPerQuestion = 'Negative marks per question cannot be negative';
        }
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveEdit = async () => {
    if (!editExam) {
      console.log('handleSaveEdit: editExam is null');
      return;
    }
    console.log('handleSaveEdit called', { editExam, errors: formErrors });
    if (!validateEdit(editExam)) {
      console.log('Validation failed', formErrors);
      return;
    }
    console.log('Validation passed, making API call');
    setIsLoading(true);
    setError(null);
    try {
      const payload: any = {
        name: editExam.name?.trim() || '',
        description: editExam.description || '',
        use_question_type_config: editExam.use_question_type_config ?? false,
        enable_negative_marking: editExam.enable_negative_marking ?? false,
      };

      if (editExam.use_question_type_config) {
        payload.question_type_settings = (editExam.question_type_settings || []).map((config: any) => {
          const setting: any = {
            question_type: config.question_type,
            num_questions: config.num_questions || 0,
            marks: config.marks || 0,
            mandatory_questions: config.mandatory_questions || 0,
          };
          // Only include negative_marks if negative marking is enabled
          if (editExam.enable_negative_marking) {
            setting.negative_marks = config.negative_marks || 0;
          }
          return setting;
        });
      } else {
        payload.total_questions = editExam.total_questions || 0;
        // Include marks if negative marking is enabled (only for simple total questions)
        if (editExam.enable_negative_marking) {
          payload.marks_per_question = editExam.marks_per_question || 0;
          payload.negative_marks_per_question = editExam.negative_marks_per_question || 0;
        }
      }

      console.log('API Payload:', payload);
      console.log('API URL:', `${API_URL}/api/target-exams/${editExam.id}`);

      await axios.put(
        `${API_URL}/api/target-exams/${editExam.id}`,
        payload,
        getAuthHeader()
      );

      console.log('API Update successful');
      await fetchTargetExams();
      setIsEditModalOpen(false);
      setEditExam(null);
      Alert.success('Success', `Target exam "${editExam.name}" updated successfully`);
    } catch (err: any) {
      console.error('API Error:', err);
      console.error('Error Response:', err?.response);
      let errorMessage = 'Failed to update target exam';
      if (err?.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err?.response?.data) {
        if (typeof err.response.data === 'string') {
          errorMessage = err.response.data;
        } else if (err.response.data.message) {
          errorMessage = err.response.data.message;
        } else {
          errorMessage = JSON.stringify(err.response.data);
        }
      } else if (err?.message) {
        errorMessage = err.message;
      }
      Alert.error('Error', errorMessage);
      setError(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteClick = (exam: TargetExam) => {
    setDeleteConfirmation({ show: true, id: exam.id, name: exam.name });
  };

  const confirmDelete = async () => {
    if (!deleteConfirmation.id) return;
    setIsLoading(true);
    setError(null);
    try {
      await axios.delete(`${API_URL}/api/target-exams/${deleteConfirmation.id}`, getAuthHeader());
      setTargetExams((prev) => prev.filter((e) => e.id !== deleteConfirmation.id));
      setDeleteConfirmation({ show: false, id: null, name: '' });
      Alert.success('Success', 'Target exam deleted successfully');
    } catch (err: any) {
      let errorMessage = 'Failed to delete target exam';
      if (err?.response?.status === 404) {
        errorMessage = 'Target exam not found';
      } else if (err?.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err?.response?.data) {
        if (typeof err.response.data === 'string') {
          errorMessage = err.response.data;
        } else if (err.response.data.message) {
          errorMessage = err.response.data.message;
        }
      } else if (err?.message) {
        errorMessage = err.message;
      }
      Alert.error('Error', errorMessage);
      setError(null);
    } finally {
      setIsLoading(false);
    }
  };

  const cancelDelete = () => {
    setDeleteConfirmation({ show: false, id: null, name: '' });
  };

  const filtered = useMemo(() => {
    if (!searchTerm) return targetExams;
    const q = searchTerm.toLowerCase();
    return targetExams.filter(
      (t) => (t.name || '').toLowerCase().includes(q) || (t.description || '').toLowerCase().includes(q)
    );
  }, [targetExams, searchTerm]);

  const pagination = usePagination(filtered, 50);

  return (
    <MainLayout user={user} onLogout={onLogout} sidebarContent={<AdminSidebar />}>
      <div className="font-kumbh bg-[#FAFBFF] min-h-screen">
        <div className="flex items-center justify-between w-full  px-8 pt-6 pb-1">
          <div className="flex flex-col items-start">
            <div className="flex items-center ">
              <h1 className="admin-title">Target Exams</h1>
            </div>
          </div>
          <button
            onClick={() => setIsModalOpen(true)}
            className="flex items-center justify-center gap-2 px-6 py-2.5 rounded-lg bg-[#111827] hover:bg-gray-800 text-white font-kumbh text-base font-semibold shadow-lg transition active:scale-95 min-w-[180px]"
          >
            <span className="text-lg"></span>
            Add New Target Exam
          </button>
        </div>

        <div className="w-full flex justify-center mt-4 ">
          <div className="bg-white rounded-lg flex items-center justify-between p-6 w-full max-w-7xl gap-4">
            <div className="flex items-center gap-3 flex-1 min-w-0 relative">
              <svg
                className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                  clipRule="evenodd"
                />
              </svg>
              <input
                type="text"
                placeholder="Search target exams..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 pr-3 py-2.5 rounded-lg border border-gray-200 shadow focus:ring-2 focus:ring-blue-200 focus:border-blue-400 bg-white text-sm font-kumbh placeholder-gray-400"
              />
            </div>
          </div>
        </div>

        <div className="flex-grow overflow-auto p-8 pt-2">
          <div className="max-w-7xl mx-auto">
            {isLoading && (
              <div className="flex justify-center py-6">
                <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
              </div>
            )}
            <div className="bg-white shadow rounded-lg overflow-hidden mt-3">
              <table className="min-w-full divide-y divide-gray-100 font-kumbh">
                <thead className="bg-white">
                  <tr>
                    <th className="px-6 py-3 text-center text-xs font-bold text-gray-600 uppercase tracking-wider w-[30%]">Name</th>
                    <th className="px-6 py-3 text-center text-xs font-bold text-gray-600 uppercase tracking-wider w-[50%]">Description</th>
                    <th className="px-6 py-3 text-center text-xs font-bold text-gray-600 uppercase tracking-wider w-[20%]">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {pagination.paginatedData.map((t) => (
                    <tr key={t.id} className="hover:bg-gray-50 transition">
                      <td className="px-6 py-3 text-base text-gray-900 text-center">{t.name}</td>
                      <td className="px-6 py-3 text-sm text-gray-600 text-center">{t.description || '-'}</td>
                      <td className="px-6 py-3 whitespace-nowrap text-center text-sm font-medium">
                        <div className="flex justify-center items-center gap-3 min-w-[140px]">
                          <button
                            onClick={() => handleEditClick(t)}
                            className="inline-flex items-center px-3 py-1.5 mr-3 text-sm font-medium text-[#111827] bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-gray-700 focus:ring-offset-2 transition-colors"
                            disabled={isLoading}
                            title="Edit"
                          >
                            <span className="w-4 h-4 mr-1.5">{FaEdit({ className: 'w-4 h-4' })}</span>
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteClick(t)}
                            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 hover:text-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors"
                            disabled={isLoading}
                            title="Delete"
                          >
                            <span className="w-4 h-4 mr-1.5">{FaTrash({ className: 'w-4 h-4' })}</span>
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={3} className="px-8 py-4 text-center text-sm text-gray-400">No target exams yet.</td>
                    </tr>
                  )}
                </tbody>
              </table>
              <Pagination {...pagination} onPageChange={pagination.setCurrentPage} onItemsPerPageChange={pagination.setItemsPerPage} />
            </div>
          </div>
        </div>
      </div>

      {/* Notification Popup */}
      {notification && (
        <div className="fixed top-4 right-4 z-[110] max-w-sm">
          <div className={`rounded-lg shadow-lg p-4 border-l-4 ${notification.type === 'success'
            ? 'bg-green-50 border-green-400 text-green-800'
            : notification.type === 'error'
              ? 'bg-red-50 border-red-400 text-red-800'
              : 'bg-blue-50 border-blue-400 text-blue-800'
            }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className={`w-2 h-2 rounded-full mr-3 ${notification.type === 'success' ? 'bg-green-400' : notification.type === 'error' ? 'bg-red-400' : 'bg-blue-400'
                  }`}></div>
                <p className="text-sm font-medium">{notification.message}</p>
              </div>
              <button onClick={() => setNotification(null)} className="ml-4 text-gray-400 hover:text-gray-600">
                {FaTimes({ className: 'w-4 h-4' })}
              </button>
            </div>
          </div>
        </div>
      )}

      {isModalOpen && (
        <div className="fixed inset-0 z-[100] overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-50"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6 max-h-[90vh] overflow-y-auto">
              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">Create Target Exam</h3>
                  {error && (
                    <div className="mt-3 bg-red-50 border-l-4 border-red-500 p-3 rounded">
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-red-700">{error}</p>
                        <button onClick={() => setError(null)} className="ml-2 text-red-500 hover:text-red-700">
                          {FaTimes({ className: 'w-4 h-4' })}
                        </button>
                      </div>
                    </div>
                  )}
                  <div className="mt-4 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-600 ">Name *</label>
                      <input
                        type="text"
                        value={form.name}
                        onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-700 focus:border-blue-500"
                        placeholder="Enter name"
                        required
                      />
                      {formErrors.name && (
                        <p className="mt-1 text-xs text-red-600">{formErrors.name}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 ">Description</label>
                      <textarea
                        rows={4}
                        value={form.description}
                        onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-700 focus:border-blue-500"
                        placeholder="Enter description"
                      />
                    </div>

                    {/* Question Type Restriction Section */}
                    <div className="border-t pt-4">
                      <label className="block text-sm font-medium text-gray-600 mb-2">Question Type Restriction</label>
                      <div className="flex items-center gap-4 mb-3">
                        <label className="flex items-center">
                          <input
                            type="radio"
                            checked={form.useQuestionTypeConfig === true}
                            onChange={() => setForm((p) => ({ ...p, useQuestionTypeConfig: true, totalQuestions: 0 }))}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-600">Yes</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="radio"
                            checked={form.useQuestionTypeConfig === false}
                            onChange={() => setForm((p) => ({ ...p, useQuestionTypeConfig: false, questionTypeConfigs: [] }))}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-600">No</span>
                        </label>
                      </div>

                      {form.useQuestionTypeConfig ? (
                        <div className="space-y-3 mt-3">
                          {formErrors.questionTypeConfigs && (
                            <p className="text-xs text-red-600">{formErrors.questionTypeConfigs}</p>
                          )}
                          {form.questionTypeConfigs.map((config, index) => (
                            <div key={index} className="border border-gray-200 rounded-md p-3 bg-gray-50">
                              <div className="flex items-center justify-between mb-3">
                                <label className="text-sm font-medium text-gray-600">Question Type {index + 1}</label>
                                <button
                                  type="button"
                                  onClick={() => removeQuestionTypeConfig(index)}
                                  className="text-red-600 hover:text-red-800 text-sm"
                                >
                                  Remove
                                </button>
                              </div>
                              <div className="space-y-3">
                                {/* First Row: Type, Number of Questions, Mandatory Questions */}
                                <div className="grid grid-cols-3 gap-3">
                                  <div>
                                    <label className="block text-xs text-gray-600 ">Question Type *</label>
                                    <select
                                      value={config.question_type}
                                      onChange={(e) => updateQuestionTypeConfig(index, 'question_type', e.target.value as QuestionType)}
                                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-gray-700 focus:border-blue-500"
                                    >
                                      {availableQuestionTypes
                                        .filter((type) => !form.questionTypeConfigs.some((c, i) => c.question_type === type && i !== index))
                                        .map((type) => (
                                          <option key={type} value={type}>
                                            {questionTypeLabels[type]}
                                          </option>
                                        ))}
                                    </select>
                                  </div>
                                  <div>
                                    <label className="block text-xs text-gray-600 ">Questions/Subject *</label>
                                    <input
                                      type="number"
                                      min="1"
                                      value={config.num_questions || ''}
                                      onChange={(e) => updateQuestionTypeConfig(index, 'num_questions', parseInt(e.target.value) || 0)}
                                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-gray-700 focus:border-blue-500"
                                    />
                                    {formErrors[`questionTypeConfig_${index}_num_questions`] && (
                                      <p className="mt-1 text-xs text-red-600">{formErrors[`questionTypeConfig_${index}_num_questions`]}</p>
                                    )}
                                  </div>
                                  <div>
                                    <label className="block text-xs text-gray-600 ">Mandatory Questions *</label>
                                    <input
                                      type="number"
                                      min="0"
                                      value={config.mandatory_questions ?? ''}
                                      onChange={(e) => updateQuestionTypeConfig(index, 'mandatory_questions', parseInt(e.target.value) || 0)}
                                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-gray-700 focus:border-blue-500"
                                    />
                                    {formErrors[`questionTypeConfig_${index}_mandatory`] && (
                                      <p className="mt-1 text-xs text-red-600">{formErrors[`questionTypeConfig_${index}_mandatory`]}</p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={addQuestionTypeConfig}
                            disabled={form.questionTypeConfigs.length >= availableQuestionTypes.length}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            + Add Question Type
                          </button>
                        </div>
                      ) : (
                        <div className="mt-3">
                          <label className="block text-sm font-medium text-gray-600 ">Total Questions *</label>
                          <input
                            type="number"
                            min="1"
                            value={form.totalQuestions || ''}
                            onChange={(e) => setForm((p) => ({ ...p, totalQuestions: parseInt(e.target.value) || 0 }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-700 focus:border-blue-500"
                            placeholder="Enter total number of questions"
                          />
                          {formErrors.totalQuestions && (
                            <p className="mt-1 text-xs text-red-600">{formErrors.totalQuestions}</p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Negative Marking Section */}
                    <div className="border-t pt-4">
                      <label className="block text-sm font-medium text-gray-600 mb-2">Negative Marking</label>
                      <div className="flex items-center gap-4 mb-3">
                        <label className="flex items-center">
                          <input
                            type="radio"
                            checked={form.enableNegativeMarking === true}
                            onChange={() => {
                              setForm((p) => {
                                const updatedConfigs = p.questionTypeConfigs.map(c => ({ ...c, negative_marks: 0 }));
                                return {
                                  ...p,
                                  enableNegativeMarking: true,
                                  marksPerQuestion: 0,
                                  negativeMarksPerQuestion: 0,
                                  questionTypeConfigs: updatedConfigs
                                };
                              });
                            }}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-600">Yes</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="radio"
                            checked={form.enableNegativeMarking === false}
                            onChange={() => {
                              setForm((p) => {
                                const updatedConfigs = p.questionTypeConfigs.map(c => ({ ...c, negative_marks: 0 }));
                                return {
                                  ...p,
                                  enableNegativeMarking: false,
                                  marksPerQuestion: 0,
                                  negativeMarksPerQuestion: 0,
                                  questionTypeConfigs: updatedConfigs
                                };
                              });
                            }}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-600">No</span>
                        </label>
                      </div>
                      <div className="mt-3">
                        {form.useQuestionTypeConfig ? (
                          // Show marks always; show negative marks only when enabled
                          <div className="space-y-3">
                            {form.questionTypeConfigs.map((config, index) => (
                              <div key={index} className="border border-gray-200 rounded-md p-3 bg-gray-50">
                                <label className="block text-sm font-medium text-gray-600 mb-3">
                                  {questionTypeLabels[config.question_type]} - Marking Scheme
                                </label>
                                <div className={form.enableNegativeMarking ? 'grid grid-cols-2 gap-3' : 'grid grid-cols-1 gap-3'}>
                                  <div>
                                    <label className="block text-xs text-gray-600 ">Marking Scheme: Correct Response *</label>
                                    <input
                                      type="number"
                                      min="0.01"
                                      step="0.01"
                                      value={config.marks || ''}
                                      onChange={(e) => updateQuestionTypeConfig(index, 'marks', parseFloat(e.target.value) || 0)}
                                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-gray-700 focus:border-blue-500"
                                    />
                                    {formErrors[`questionTypeConfig_${index}_marks`] && (
                                      <p className="mt-1 text-xs text-red-600">{formErrors[`questionTypeConfig_${index}_marks`]}</p>
                                    )}
                                  </div>
                                  {form.enableNegativeMarking && (
                                    <div>
                                      <label className="block text-xs text-gray-600 ">Incorrect Response *</label>
                                      <input
                                        type="text"
                                        value={config.negative_marks ? `-${config.negative_marks}` : ''}
                                        onChange={(e) => {
                                          const inputValue = e.target.value.replace(/^-/, '').replace(/[^0-9.]/g, '');
                                          const numValue = parseFloat(inputValue) || 0;
                                          updateQuestionTypeConfig(index, 'negative_marks', numValue);
                                        }}
                                        onBlur={(e) => {
                                          const inputValue = e.target.value.replace(/^-/, '').replace(/[^0-9.]/g, '');
                                          const numValue = parseFloat(inputValue) || 0;
                                          updateQuestionTypeConfig(index, 'negative_marks', numValue);
                                        }}
                                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-gray-700 focus:border-blue-500"
                                        placeholder="e.g., -1.0"
                                      />
                                      {formErrors[`questionTypeConfig_${index}_negative_marks`] && (
                                        <p className="mt-1 text-xs text-red-600">{formErrors[`questionTypeConfig_${index}_negative_marks`]}</p>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          form.enableNegativeMarking && (
                            // Show global marking scheme for simple total questions only when negative marking is enabled
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="block text-sm font-medium text-gray-600 ">Marks Per Question *</label>
                                <input
                                  type="number"
                                  min="0.01"
                                  step="0.01"
                                  value={form.marksPerQuestion || ''}
                                  onChange={(e) => setForm((p) => ({ ...p, marksPerQuestion: parseFloat(e.target.value) || 0 }))}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-700 focus:border-blue-500"
                                  placeholder="e.g., 4.0"
                                />
                                {formErrors.marksPerQuestion && (
                                  <p className="mt-1 text-xs text-red-600">{formErrors.marksPerQuestion}</p>
                                )}
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-600 ">Negative Marks Per Question *</label>
                                <input
                                  type="text"
                                  value={form.negativeMarksPerQuestion ? `-${form.negativeMarksPerQuestion}` : ''}
                                  onChange={(e) => {
                                    const inputValue = e.target.value.replace(/^-/, '').replace(/[^0-9.]/g, '');
                                    const numValue = parseFloat(inputValue) || 0;
                                    setForm((p) => ({ ...p, negativeMarksPerQuestion: numValue }));
                                  }}
                                  onBlur={(e) => {
                                    const inputValue = e.target.value.replace(/^-/, '').replace(/[^0-9.]/g, '');
                                    const numValue = parseFloat(inputValue) || 0;
                                    setForm((p) => ({ ...p, negativeMarksPerQuestion: numValue }));
                                  }}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-700 focus:border-blue-500"
                                  placeholder="e.g., -1.0"
                                />
                                {formErrors.negativeMarksPerQuestion && (
                                  <p className="mt-1 text-xs text-red-600">{formErrors.negativeMarksPerQuestion}</p>
                                )}
                              </div>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-6 sm:flex sm:flex-row-reverse gap-2">
                <button
                  type="button"
                  onClick={handleCreate}
                  disabled={isSaving}
                  className="w-full inline-flex justify-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-[#111827] text-sm font-medium text-white hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-700 sm:ml-3 sm:w-auto sm:text-sm transition active:scale-95"
                >
                  {isSaving ? 'Creating...' : 'Create'}
                </button>
                <button
                  type="button"
                  onClick={() => { setIsModalOpen(false); resetForm(); }}
                  className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-600 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-300 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isEditModalOpen && editExam && (
        <div className="fixed inset-0 z-[100] overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-50"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6 max-h-[90vh] overflow-y-auto">
              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">Edit Target Exam</h3>
                  {error && (
                    <div className="mt-3 bg-red-50 border-l-4 border-red-500 p-3 rounded">
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-red-700">{error}</p>
                        <button onClick={() => setError(null)} className="ml-2 text-red-500 hover:text-red-700">
                          {FaTimes({ className: 'w-4 h-4' })}
                        </button>
                      </div>
                    </div>
                  )}
                  <div className="mt-4 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-600 ">Name *</label>
                      <input
                        type="text"
                        value={editExam.name || ''}
                        onChange={(e) => setEditExam({ ...editExam, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-700 focus:border-blue-500"
                        placeholder="Enter name"
                        required
                      />
                      {formErrors.name && (
                        <p className="mt-1 text-xs text-red-600">{formErrors.name}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 ">Description</label>
                      <textarea
                        rows={4}
                        value={editExam.description || ''}
                        onChange={(e) => setEditExam({ ...editExam, description: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-700 focus:border-blue-500"
                        placeholder="Enter description"
                      />
                    </div>

                    {/* Question Type Restriction Section */}
                    <div className="border-t pt-4">
                      <label className="block text-sm font-medium text-gray-600 mb-2">Question Type Restriction</label>
                      <div className="flex items-center gap-4 mb-3">
                        <label className="flex items-center">
                          <input
                            type="radio"
                            checked={editExam.use_question_type_config === true}
                            onChange={() => setEditExam({ ...editExam, use_question_type_config: true, total_questions: 0 })}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-600">Yes</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="radio"
                            checked={editExam.use_question_type_config === false}
                            onChange={() => setEditExam({ ...editExam, use_question_type_config: false, question_type_settings: [] })}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-600">No</span>
                        </label>
                      </div>

                      {editExam.use_question_type_config ? (
                        <div className="space-y-3 mt-3">
                          {formErrors.questionTypeConfigs && (
                            <p className="text-xs text-red-600">{formErrors.questionTypeConfigs}</p>
                          )}
                          {(editExam.question_type_settings || []).map((config: any, index: number) => {
                            const numQuestions = config.num_questions || 0;
                            const marks = config.marks || 0;
                            const negativeMarks = config.negative_marks || 0;
                            const mandatoryQuestions = config.mandatory_questions || 0;
                            const questionType = config.question_type || 'single_correct_A';

                            return (
                              <div key={index} className="border border-gray-200 rounded-md p-3 bg-gray-50">
                                <div className="flex items-center justify-between mb-3">
                                  <label className="text-sm font-medium text-gray-600">Question Type {index + 1}</label>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const updatedConfigs = (editExam.question_type_settings || []).filter((_: any, i: number) => i !== index);
                                      setEditExam({ ...editExam, question_type_settings: updatedConfigs });
                                    }}
                                    className="text-red-600 hover:text-red-800 text-sm"
                                  >
                                    Remove
                                  </button>
                                </div>
                                <div className="space-y-3">
                                  {/* First Row: Type, Number of Questions, Mandatory Questions */}
                                  <div className="grid grid-cols-3 gap-3">
                                    <div>
                                      <label className="block text-xs text-gray-600 ">Question Type *</label>
                                      <select
                                        value={questionType}
                                        onChange={(e) => {
                                          const updatedConfigs = (editExam.question_type_settings || []).map((c: any, i: number) =>
                                            i === index ? { ...c, question_type: e.target.value } : c
                                          );
                                          setEditExam({ ...editExam, question_type_settings: updatedConfigs });
                                        }}
                                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-gray-700 focus:border-blue-500"
                                      >
                                        {availableQuestionTypes
                                          .filter((type) => !(editExam.question_type_settings || []).some((c: any, i: number) => c.question_type === type && i !== index))
                                          .map((type) => (
                                            <option key={type} value={type}>
                                              {questionTypeLabels[type]}
                                            </option>
                                          ))}
                                      </select>
                                    </div>
                                    <div>
                                      <label className="block text-xs text-gray-600 ">Questions/Subject *</label>
                                      <input
                                        type="number"
                                        min="1"
                                        value={numQuestions || ''}
                                        onChange={(e) => {
                                          const updatedConfigs = (editExam.question_type_settings || []).map((c: any, i: number) =>
                                            i === index ? { ...c, num_questions: parseInt(e.target.value) || 0 } : c
                                          );
                                          setEditExam({ ...editExam, question_type_settings: updatedConfigs });
                                        }}
                                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-gray-700 focus:border-blue-500"
                                      />
                                      {formErrors[`questionTypeConfig_${index}_num_questions`] && (
                                        <p className="mt-1 text-xs text-red-600">{formErrors[`questionTypeConfig_${index}_num_questions`]}</p>
                                      )}
                                    </div>
                                    <div>
                                      <label className="block text-xs text-gray-600 ">Mandatory Questions *</label>
                                      <input
                                        type="number"
                                        min="0"
                                        value={mandatoryQuestions ?? ''}
                                        onChange={(e) => {
                                          const updatedConfigs = (editExam.question_type_settings || []).map((c: any, i: number) =>
                                            i === index ? { ...c, mandatory_questions: parseInt(e.target.value) || 0 } : c
                                          );
                                          setEditExam({ ...editExam, question_type_settings: updatedConfigs });
                                        }}
                                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-gray-700 focus:border-blue-500"
                                      />
                                      {formErrors[`questionTypeConfig_${index}_mandatory`] && (
                                        <p className="mt-1 text-xs text-red-600">{formErrors[`questionTypeConfig_${index}_mandatory`]}</p>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                          <button
                            type="button"
                            onClick={() => {
                              const availableTypes = availableQuestionTypes.filter(
                                (type) => !(editExam.question_type_settings || []).some((c: any) => c.question_type === type)
                              );
                              if (availableTypes.length > 0) {
                                const updatedConfigs = [
                                  ...(editExam.question_type_settings || []),
                                  { question_type: availableTypes[0], num_questions: 0, marks: 0, negative_marks: 0, mandatory_questions: 0 },
                                ];
                                setEditExam({ ...editExam, question_type_settings: updatedConfigs });
                              }
                            }}
                            disabled={(editExam.question_type_settings || []).length >= availableQuestionTypes.length}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            + Add Question Type
                          </button>
                        </div>
                      ) : (
                        <div className="mt-3">
                          <label className="block text-sm font-medium text-gray-600 ">Total Questions *</label>
                          <input
                            type="number"
                            min="1"
                            value={editExam.total_questions || ''}
                            onChange={(e) => setEditExam({ ...editExam, total_questions: parseInt(e.target.value) || 0 })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-700 focus:border-blue-500"
                            placeholder="Enter total number of questions"
                          />
                          {formErrors.totalQuestions && (
                            <p className="mt-1 text-xs text-red-600">{formErrors.totalQuestions}</p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Negative Marking Section */}
                    <div className="border-t pt-4">
                      <label className="block text-sm font-medium text-gray-600 mb-2">Negative Marking</label>
                      <div className="flex items-center gap-4 mb-3">
                        <label className="flex items-center">
                          <input
                            type="radio"
                            checked={editExam.enable_negative_marking === true}
                            onChange={() => {
                              const updatedConfigs = (editExam.question_type_settings || []).map((c: any) => ({ ...c, negative_marks: 0 }));
                              setEditExam({
                                ...editExam,
                                enable_negative_marking: true,
                                marks_per_question: 0,
                                negative_marks_per_question: 0,
                                question_type_settings: updatedConfigs
                              });
                            }}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-600">Yes</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="radio"
                            checked={editExam.enable_negative_marking === false}
                            onChange={() => {
                              const updatedConfigs = (editExam.question_type_settings || []).map((c: any) => ({ ...c, negative_marks: 0 }));
                              setEditExam({
                                ...editExam,
                                enable_negative_marking: false,
                                marks_per_question: 0,
                                negative_marks_per_question: 0,
                                question_type_settings: updatedConfigs
                              });
                            }}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-600">No</span>
                        </label>
                      </div>
                      <div className="mt-3">
                        {editExam.use_question_type_config ? (
                          // Show marks always; show negative marks only when enabled
                          <div className="space-y-3">
                            {(editExam.question_type_settings || []).map((config: any, index: number) => {
                              const questionType: QuestionType = (config.question_type || 'single_correct_A') as QuestionType;
                              const marks = config.marks || 0;
                              const negativeMarks = config.negative_marks || 0;
                              const typeLabel = questionTypeLabels[questionType] || 'Unknown Type';

                              return (
                                <div key={index} className="border border-gray-200 rounded-md p-3 bg-gray-50">
                                  <label className="block text-sm font-medium text-gray-600 mb-3">
                                    {typeLabel} - Marking Scheme
                                  </label>
                                  <div className={editExam.enable_negative_marking ? 'grid grid-cols-2 gap-3' : 'grid grid-cols-1 gap-3'}>
                                    <div>
                                      <label className="block text-xs text-gray-600 ">Marking Scheme: Correct Response *</label>
                                      <input
                                        type="number"
                                        min="0.01"
                                        step="0.01"
                                        value={marks || ''}
                                        onChange={(e) => {
                                          const updatedConfigs = (editExam.question_type_settings || []).map((c: any, i: number) =>
                                            i === index ? { ...c, marks: parseFloat(e.target.value) || 0 } : c
                                          );
                                          setEditExam({ ...editExam, question_type_settings: updatedConfigs });
                                        }}
                                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-gray-700 focus:border-blue-500"
                                      />
                                      {formErrors[`questionTypeConfig_${index}_marks`] && (
                                        <p className="mt-1 text-xs text-red-600">{formErrors[`questionTypeConfig_${index}_marks`]}</p>
                                      )}
                                    </div>
                                    {editExam.enable_negative_marking && (
                                      <div>
                                        <label className="block text-xs text-gray-600 ">Incorrect Response *</label>
                                        <input
                                          type="text"
                                          value={negativeMarks ? `-${negativeMarks}` : ''}
                                          onChange={(e) => {
                                            const inputValue = e.target.value.replace(/^-/, '').replace(/[^0-9.]/g, '');
                                            const numValue = parseFloat(inputValue) || 0;
                                            const updatedConfigs = (editExam.question_type_settings || []).map((c: any, i: number) =>
                                              i === index ? { ...c, negative_marks: numValue } : c
                                            );
                                            setEditExam({ ...editExam, question_type_settings: updatedConfigs });
                                          }}
                                          onBlur={(e) => {
                                            const inputValue = e.target.value.replace(/^-/, '').replace(/[^0-9.]/g, '');
                                            const numValue = parseFloat(inputValue) || 0;
                                            const updatedConfigs = (editExam.question_type_settings || []).map((c: any, i: number) =>
                                              i === index ? { ...c, negative_marks: numValue } : c
                                            );
                                            setEditExam({ ...editExam, question_type_settings: updatedConfigs });
                                          }}
                                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-gray-700 focus:border-blue-500"
                                          placeholder="e.g., -1.0"
                                        />
                                        {formErrors[`questionTypeConfig_${index}_negative_marks`] && (
                                          <p className="mt-1 text-xs text-red-600">{formErrors[`questionTypeConfig_${index}_negative_marks`]}</p>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          editExam.enable_negative_marking && (
                            // Show global marking scheme for simple total questions only when negative marking is enabled
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="block text-sm font-medium text-gray-600 ">Marks Per Question *</label>
                                <input
                                  type="number"
                                  min="0.01"
                                  step="0.01"
                                  value={editExam.marks_per_question || ''}
                                  onChange={(e) => setEditExam({ ...editExam, marks_per_question: parseFloat(e.target.value) || 0 })}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-700 focus:border-blue-500"
                                  placeholder="e.g., 4.0"
                                />
                                {formErrors.marksPerQuestion && (
                                  <p className="mt-1 text-xs text-red-600">{formErrors.marksPerQuestion}</p>
                                )}
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-600 ">Negative Marks Per Question *</label>
                                <input
                                  type="text"
                                  value={editExam.negative_marks_per_question ? `-${editExam.negative_marks_per_question}` : ''}
                                  onChange={(e) => {
                                    const inputValue = e.target.value.replace(/^-/, '').replace(/[^0-9.]/g, '');
                                    const numValue = parseFloat(inputValue) || 0;
                                    setEditExam({ ...editExam, negative_marks_per_question: numValue });
                                  }}
                                  onBlur={(e) => {
                                    const inputValue = e.target.value.replace(/^-/, '').replace(/[^0-9.]/g, '');
                                    const numValue = parseFloat(inputValue) || 0;
                                    setEditExam({ ...editExam, negative_marks_per_question: numValue });
                                  }}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-700 focus:border-blue-500"
                                  placeholder="e.g., -1.0"
                                />
                                {formErrors.negativeMarksPerQuestion && (
                                  <p className="mt-1 text-xs text-red-600">{formErrors.negativeMarksPerQuestion}</p>
                                )}
                              </div>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-6 sm:flex sm:flex-row-reverse gap-2">
                <button
                  type="button"
                  onClick={handleSaveEdit}
                  disabled={isLoading}
                  className="w-full inline-flex justify-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-[#111827] text-sm font-medium text-white hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-700 sm:ml-3 sm:w-auto sm:text-sm transition active:scale-95"
                >
                  {isLoading ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  type="button"
                  onClick={() => { setIsEditModalOpen(false); setEditExam(null); }}
                  className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-600 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-300 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {deleteConfirmation.show && (
        <div className="fixed inset-0 z-[100] overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-50"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                  {FaTrash({ className: 'h-6 w-6 text-red-600' })}
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">Delete Target Exam</h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-600">Are you sure you want to delete "{deleteConfirmation.name}"? This action cannot be undone.</p>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={confirmDelete}
                  className="w-full inline-flex justify-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-red-600 text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm transition active:scale-95"
                >
                  Delete
                </button>
                <button
                  type="button"
                  onClick={cancelDelete}
                  className="mt-3 w-full inline-flex justify-center rounded-lg border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-600 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-700 sm:mt-0 sm:w-auto sm:text-sm transition active:scale-95"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default TargetExamPage;




